import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Edit,
  Eye,
  EyeOff,
  FileQuestion,
  MoreHorizontal,
  Plus,
  SearchX,
  Star,
  Trash2,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTable } from '../../components/admin/DataTable.jsx';
import { EmptyState } from '../../components/admin/EmptyState.jsx';
import { FilterBar } from '../../components/admin/FilterBar.jsx';
import { FormDrawer } from '../../components/admin/FormDrawer.jsx';
import { PageHeader } from '../../components/admin/PageHeader.jsx';
import { Pagination } from '../../components/admin/Pagination.jsx';
import { StatusBadge } from '../../components/admin/StatusBadge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { useConfirm } from '../../components/ui/ConfirmDialog.jsx';
import { ImageUploadField } from '../../components/ui/ImageUploadField.jsx';
import { RichTextEditor } from '../../components/ui/RichTextEditor.jsx';
import { useDebouncedValue } from '../../hooks/useDebouncedValue.js';
import { usePermissions } from '../../hooks/usePermissions.js';
import { api } from '../../services/api.js';
import { notify } from '../../services/notify.js';

let benefitKey = 0;
const nextBenefitKey = () => {
  benefitKey += 1;
  return `benefit-${benefitKey}`;
};

const emptyForm = {
  titleFr: '',
  titleEn: '',
  summaryFr: '',
  summaryEn: '',
  descriptionFr: '',
  descriptionEn: '',
  benefits: [],
  image: '',
  gallery: [],
  client: '',
  category: '',
  roleFr: '',
  roleEn: '',
  company: '',
  videoUrl: '',
  featured: false,
  tags: '',
  status: 'published',
};

const getImage = (item) => item.image || item.coverImage || item.url || item.thumbnail || '';

const galleryUrls = (item) => {
  const cover = getImage(item);
  const urls = (item.gallery || []).map((image) => image.url || image).filter(Boolean);
  return [cover, ...urls].filter((url, index, list) => url && list.indexOf(url) === index).slice(0, 5);
};

const toForm = (item) => ({
  titleFr: item.title?.fr || '',
  titleEn: item.title?.en || '',
  summaryFr: item.summary?.fr || item.excerpt?.fr || '',
  summaryEn: item.summary?.en || item.excerpt?.en || '',
  descriptionFr: item.description?.fr || item.content?.fr || '',
  descriptionEn: item.description?.en || item.content?.en || '',
  benefits: Array.isArray(item.benefits)
    ? item.benefits.map((benefit) => ({ id: nextBenefitKey(), fr: benefit?.fr || '', en: benefit?.en || '' }))
    : [],
  image: getImage(item),
  gallery: galleryUrls(item),
  client: item.client || '',
  category: item.category || '',
  roleFr: item.role?.fr || '',
  roleEn: item.role?.en || '',
  company: item.company || '',
  videoUrl: item.videoUrl || '',
  featured: Boolean(item.featured),
  tags: (item.tags || []).join(', '),
  status: item.status || 'published',
});

const imagesToGallery = (form) =>
  form.gallery.slice(0, 5).map((url) => ({
    url,
    alt: { fr: form.titleFr, en: form.titleEn },
  }));

const localizedRows = (rows) =>
  (rows || [])
    .map((row) => ({ fr: row.fr?.trim() || '', en: row.en?.trim() || '' }))
    .filter((row) => row.fr || row.en);

const payloadByResource = (resource, form) => {
  const base = {
    title: { fr: form.titleFr, en: form.titleEn },
    status: form.status,
    tags: form.tags
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean),
  };

  if (resource === 'services') {
    return {
      ...base,
      summary: { fr: form.summaryFr, en: form.summaryEn },
      description: { fr: form.descriptionFr, en: form.descriptionEn },
      benefits: localizedRows(form.benefits),
      image: form.gallery[0] || form.image,
      gallery: imagesToGallery(form),
    };
  }

  if (resource === 'projects') {
    return {
      ...base,
      client: form.client,
      category: form.category || 'OOH',
      videoUrl: form.videoUrl,
      featured: Boolean(form.featured),
      summary: { fr: form.summaryFr, en: form.summaryEn },
      description: { fr: form.descriptionFr, en: form.descriptionEn },
      coverImage: form.gallery[0] || form.image,
      gallery: imagesToGallery(form),
    };
  }

  if (resource === 'posts') {
    return {
      ...base,
      excerpt: { fr: form.summaryFr, en: form.summaryEn },
      content: { fr: form.descriptionFr, en: form.descriptionEn },
      coverImage: form.gallery[0] || form.image,
      gallery: imagesToGallery(form),
      publishedAt: new Date().toISOString(),
    };
  }

  if (resource === 'testimonials') {
    return {
      ...base,
      summary: { fr: form.summaryFr, en: form.summaryEn },
      role: { fr: form.roleFr, en: form.roleEn },
      company: form.company,
    };
  }

  return {
    title: base.title,
    type: 'image',
    url: form.image || form.gallery[0],
    thumbnail: form.image || form.gallery[0],
    category: form.category || 'campaign',
    tags: base.tags,
    status: form.status,
  };
};

const fieldDefs = (resource, t) => {
  const common = [
    ['titleFr', t('admin.titleFr'), true],
    ['titleEn', t('admin.titleEn'), false],
    ['summaryFr', t('admin.summaryFr'), false],
    ['summaryEn', t('admin.summaryEn'), false],
  ];

  if (resource === 'projects') {
    return [
      ...common,
      ['client', t('admin.fieldClient'), false],
      ['category', t('admin.fieldCategory'), false],
      ['videoUrl', t('admin.fieldVideoUrl'), false],
      ['tags', t('admin.fieldTags'), false],
    ];
  }

  if (resource === 'testimonials') {
    return [
      ['titleFr', t('admin.fieldAuthorFr'), true],
      ['titleEn', t('admin.fieldAuthorEn'), false],
      ['summaryFr', t('admin.fieldQuoteFr'), false],
      ['summaryEn', t('admin.fieldQuoteEn'), false],
      ['roleFr', t('admin.fieldRoleFr'), false],
      ['roleEn', t('admin.fieldRoleEn'), false],
      ['company', t('admin.fieldCompany'), false],
    ];
  }

  if (resource === 'gallery') {
    return [
      ['titleFr', t('admin.titleFr'), true],
      ['titleEn', t('admin.titleEn'), false],
      ['category', t('admin.fieldCategory'), false],
      ['tags', t('admin.fieldTags'), false],
    ];
  }

  return [...common, ['tags', t('admin.fieldTags'), false]];
};

const validate = (resource, form, t) => {
  const errors = {};
  if (!form.titleFr.trim() && !form.titleEn.trim()) {
    errors.titleFr = t('admin.errorTitleRequired');
  }
  if (resource === 'projects' && form.videoUrl && !/^https?:\/\//i.test(form.videoUrl)) {
    errors.videoUrl = t('admin.errorUrlInvalid');
  }
  if (resource === 'testimonials' && !form.summaryFr.trim() && !form.summaryEn.trim()) {
    errors.summaryFr = t('admin.errorQuoteRequired');
  }
  return errors;
};

const DRAFT_KEY = (resource) => `beam-draft-${resource}`;

export default function AdminCollectionPage({ resource }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const permissions = usePermissions();

  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(12);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');

  const debouncedSearch = useDebouncedValue(search);
  const title = t(`admin.${resource}`, { defaultValue: resource });

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, status, limit]);

  const query = useQuery({
    queryKey: [resource, page, limit, debouncedSearch, status],
    queryFn: () =>
      api
        .get(`/${resource}`, {
          params: {
            page,
            limit,
            search: debouncedSearch || undefined,
            status: status === 'all' ? undefined : status,
          },
        })
        .then((response) => response.data),
    placeholderData: keepPreviousData,
  });

  const items = useMemo(() => query.data?.data || [], [query.data]);
  const pagination = query.data?.pagination || { page: 1, pages: 1, total: items.length, limit };
  const allVisibleSelected =
    items.length > 0 && items.every((item) => selectedIds.includes(item._id || item.id));
  const hasFilters = Boolean(search || status !== 'all');

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: [resource] });
    if (resource === 'services') {
      queryClient.invalidateQueries({ queryKey: ['public-services'] });
      queryClient.invalidateQueries({ queryKey: ['public-service'] });
    }
    setSelectedIds([]);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setErrors({});
    setDirty(false);
    localStorage.removeItem(DRAFT_KEY(resource));
  };

  const saveMutation = useMutation({
    mutationFn: (payload) =>
      editing
        ? api.patch(`/${resource}/${editing._id || editing.id}`, payload).then((response) => response.data)
        : api.post(`/${resource}`, payload).then((response) => response.data),
    onSuccess: () => {
      invalidate();
      notify.success(editing ? t('common.saved') : t('common.created'));
      setForm(emptyForm);
      closeForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/${resource}/${id}`),
    onSuccess: () => {
      invalidate();
      notify.success(t('common.deleted'));
    },
  });

  const bulkMutation = useMutation({
    mutationFn: (action) =>
      api.patch(`/${resource}/bulk`, { ids: selectedIds, action }).then((response) => response.data),
    onSuccess: (_data, action) => {
      invalidate();
      notify.success(t('admin.bulkDone', { action: t(`admin.bulkAction_${action}`, { defaultValue: action }) }));
    },
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, nextStatus }) =>
      api.patch(`/${resource}/${id}`, { status: nextStatus }).then((response) => response.data),
    onSuccess: () => {
      invalidate();
      notify.success(t('common.saved'));
    },
  });

  const featuredMutation = useMutation({
    mutationFn: ({ id, featured }) =>
      api.patch(`/${resource}/${id}`, { featured }).then((response) => response.data),
    onSuccess: () => {
      invalidate();
      notify.success(t('common.saved'));
    },
  });

  const update = (event) => {
    const { checked, name, type, value } = event.target;
    setDirty(true);
    setErrors((current) => ({ ...current, [name]: undefined }));
    setForm((current) => ({ ...current, [name]: type === 'checkbox' ? checked : value }));
  };

  const patchForm = (patch) => {
    setDirty(true);
    setForm((current) => ({ ...current, ...patch }));
  };

  // A dropped connection used to cost the whole entry.
  useEffect(() => {
    if (!showForm || !dirty) return;
    localStorage.setItem(DRAFT_KEY(resource), JSON.stringify({ form, editingId: editing?._id || null }));
  }, [showForm, dirty, form, editing, resource]);

  const openCreate = () => {
    const stored = localStorage.getItem(DRAFT_KEY(resource));
    if (stored) {
      try {
        const draft = JSON.parse(stored);
        if (draft?.form && !draft.editingId) {
          setForm(draft.form);
          setDirty(true);
          setEditing(null);
          setErrors({});
          setShowForm(true);
          notify.info(t('admin.draftRestored'));
          return;
        }
      } catch {
        localStorage.removeItem(DRAFT_KEY(resource));
      }
    }
    setForm(emptyForm);
    setEditing(null);
    setErrors({});
    setDirty(false);
    setShowForm(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm(toForm(item));
    setErrors({});
    setDirty(false);
    setShowForm(true);
  };

  const requestClose = async () => {
    if (!dirty) return closeForm();
    const confirmed = await confirm({
      title: t('admin.discardChangesTitle'),
      description: t('admin.discardChangesBody'),
      confirmLabel: t('admin.discardChanges'),
      tone: 'danger',
    });
    if (confirmed) closeForm();
    return undefined;
  };

  const submit = (event) => {
    event.preventDefault();
    const nextErrors = validate(resource, form, t);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      notify.error(t('admin.errorFixFields'));
      return;
    }
    saveMutation.mutate(payloadByResource(resource, form));
  };

  const requestDelete = async (item) => {
    const label = item.title?.fr || item.title?.en || item.slug || t('admin.thisRecord');
    const confirmed = await confirm({
      title: t('admin.deleteRecordTitle'),
      description: t('admin.deleteRecordBody', { label }),
      confirmLabel: t('common.delete'),
    });
    if (confirmed) deleteMutation.mutate(item._id || item.id);
  };

  const requestBulk = async (action) => {
    if (action === 'delete') {
      const confirmed = await confirm({
        title: t('admin.bulkDeleteTitle', { count: selectedIds.length }),
        description: t('admin.bulkDeleteBody', { count: selectedIds.length }),
        confirmLabel: t('common.delete'),
        requireText: t('admin.deleteKeyword'),
      });
      if (!confirmed) return;
    }
    bulkMutation.mutate(action);
  };

  const toggleSelected = (id) =>
    setSelectedIds((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
    );

  const toggleAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedIds((current) =>
        current.filter((id) => !items.some((item) => (item._id || item.id) === id)),
      );
    } else {
      setSelectedIds((current) =>
        Array.from(new Set([...current, ...items.map((item) => item._id || item.id)])),
      );
    }
  };

  const usesGallery = ['services', 'projects', 'posts'].includes(resource);

  const rowActions = (item) => {
    const id = item._id || item.id;
    return (
      <div className="flex flex-wrap justify-end gap-1.5">
        {resource === 'projects' ? (
          <button
            type="button"
            onClick={() => featuredMutation.mutate({ id, featured: !item.featured })}
            className={`focus-ring inline-flex h-9 w-9 items-center justify-center rounded-lg ${
              item.featured ? 'bg-beam-yellow/30 text-beam-ink' : 'bg-beam-light text-beam-graphite'
            }`}
            aria-label={item.featured ? t('admin.unfeature') : t('admin.feature')}
          >
            <Star className={`h-4 w-4 ${item.featured ? 'fill-current' : ''}`} aria-hidden="true" />
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => openEdit(item)}
          className="focus-ring inline-flex h-9 w-9 items-center justify-center rounded-lg bg-beam-light text-beam-blue"
          aria-label={t('common.edit')}
        >
          <Edit className="h-4 w-4" aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={() =>
            statusMutation.mutate({ id, nextStatus: item.status === 'hidden' ? 'published' : 'hidden' })
          }
          className="focus-ring inline-flex h-9 w-9 items-center justify-center rounded-lg bg-beam-light text-beam-graphite"
          aria-label={item.status === 'hidden' ? t('admin.show') : t('admin.hide')}
        >
          {item.status === 'hidden' ? (
            <Eye className="h-4 w-4" aria-hidden="true" />
          ) : (
            <EyeOff className="h-4 w-4" aria-hidden="true" />
          )}
        </button>
        {permissions.canDeleteContent ? (
          <button
            type="button"
            onClick={() => requestDelete(item)}
            className="focus-ring inline-flex h-9 w-9 items-center justify-center rounded-lg bg-state-critical/10 text-state-critical"
            aria-label={t('common.delete')}
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
          </button>
        ) : null}
      </div>
    );
  };

  const thumbnail = (item) => {
    const image = getImage(item);
    if (image) {
      return (
        <img
          src={image}
          alt=""
          loading="lazy"
          decoding="async"
          className="h-14 w-14 rounded-lg bg-beam-light object-cover"
        />
      );
    }
    return (
      <div className="grid h-14 w-14 place-items-center rounded-lg bg-beam-light text-lg font-extrabold text-beam-blue">
        {(item.title?.fr || item.title?.en || item.slug || '?').slice(0, 1).toUpperCase()}
      </div>
    );
  };

  const columns = [
    {
      key: 'thumb',
      header: <span className="sr-only">{t('admin.coverImage')}</span>,
      className: 'w-20',
      cell: thumbnail,
    },
    {
      key: 'title',
      header: t('admin.columnTitle'),
      cell: (item) => (
        <div className="min-w-0">
          <p className="truncate font-bold text-beam-ink">
            {item.title?.fr || item.title?.en || item.originalName || item.slug}
          </p>
          <p className="mt-0.5 truncate text-xs font-semibold text-beam-graphite/60">
            {item.slug || item.category || ''}
            {item.gallery?.length ? ` · ${t('admin.imageCount', { count: item.gallery.length })}` : ''}
          </p>
        </div>
      ),
    },
    {
      key: 'status',
      header: t('admin.status'),
      className: 'w-36',
      cell: (item) => (
        <div className="flex flex-wrap gap-1.5">
          <StatusBadge status={item.status} />
          {resource === 'projects' && item.featured ? (
            <span className="inline-flex items-center gap-1 rounded-md bg-beam-yellow/25 px-2 py-1 text-xs font-extrabold text-beam-ink">
              <Star className="h-3 w-3 fill-current" aria-hidden="true" />
              {t('admin.featured')}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'actions',
      header: <span className="sr-only">{t('admin.actions')}</span>,
      className: 'w-52 text-right',
      cell: rowActions,
    },
  ];

  const emptyNode = hasFilters ? (
    <EmptyState
      icon={SearchX}
      title={t('admin.noMatchesTitle')}
      description={t('admin.noMatchesBody')}
      action={
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            setSearch('');
            setStatus('all');
          }}
        >
          {t('admin.clearFilters')}
        </Button>
      }
    />
  ) : (
    <EmptyState
      icon={FileQuestion}
      title={t('admin.noRecordsTitle', { resource: title.toLowerCase() })}
      description={t('admin.noRecordsBody')}
      action={
        permissions.canEditContent ? (
          <Button type="button" icon={Plus} onClick={openCreate}>
            {t('common.create')}
          </Button>
        ) : null
      }
    />
  );

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={t('admin.content')}
        title={title}
        description={t('admin.collectionHelp')}
        actions={
          permissions.canEditContent ? (
            <Button type="button" icon={Plus} onClick={openCreate}>
              {t('common.create')}
            </Button>
          ) : null
        }
      />

      <FilterBar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder={t('admin.searchContent')}
        selects={[
          {
            id: 'filter-status',
            label: t('admin.status'),
            value: status,
            onChange: setStatus,
            options: [
              { value: 'all', label: t('common.all') },
              { value: 'published', label: t('admin.published') },
              { value: 'draft', label: t('common.draft') },
              { value: 'hidden', label: t('admin.hidden') },
            ],
          },
          {
            id: 'filter-limit',
            label: t('admin.perPage'),
            value: String(limit),
            onChange: (value) => setLimit(Number(value)),
            options: [12, 24, 48].map((size) => ({ value: String(size), label: String(size) })),
          },
        ]}
        activeFilters={[
          search ? { id: 'search', label: `“${search}”`, onRemove: () => setSearch('') } : null,
          status !== 'all'
            ? { id: 'status', label: t(`admin.${status}`, { defaultValue: status }), onRemove: () => setStatus('all') }
            : null,
        ].filter(Boolean)}
        onClear={
          hasFilters
            ? () => {
                setSearch('');
                setStatus('all');
              }
            : undefined
        }
      />

      {selectedIds.length ? (
        <div className="sticky top-24 z-20 flex flex-wrap items-center justify-between gap-3 rounded-lg bg-beam-ink p-3 text-white shadow-crisp">
          <p className="text-sm font-extrabold">
            {t('admin.selectedCount', { count: selectedIds.length })}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" variant="light" icon={Eye} onClick={() => requestBulk('publish')}>
              {t('admin.publish')}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              icon={MoreHorizontal}
              onClick={() => requestBulk('draft')}
            >
              {t('admin.moveDraft')}
            </Button>
            <Button type="button" size="sm" variant="outline" icon={EyeOff} onClick={() => requestBulk('hide')}>
              {t('admin.hide')}
            </Button>
            {permissions.canDeleteContent ? (
              <Button type="button" size="sm" variant="danger" icon={Trash2} onClick={() => requestBulk('delete')}>
                {t('common.delete')}
              </Button>
            ) : null}
            <Button type="button" size="sm" variant="outline" onClick={() => setSelectedIds([])}>
              {t('admin.clearSelection')}
            </Button>
          </div>
        </div>
      ) : null}

      <DataTable
        caption={t('admin.tableCaption', { resource: title })}
        columns={columns}
        rows={items}
        getRowId={(item) => item._id || item.id}
        selectable
        selectedIds={selectedIds}
        onToggleRow={toggleSelected}
        onToggleAll={toggleAllVisible}
        allSelected={allVisibleSelected}
        isLoading={query.isLoading}
        isFetching={query.isFetching}
        empty={emptyNode}
        renderCard={(item) => (
          <div>
            <div className="flex items-start gap-3">
              {thumbnail(item)}
              <div className="min-w-0 flex-1">
                <p className="font-bold text-beam-ink">{item.title?.fr || item.title?.en || item.slug}</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <StatusBadge status={item.status} />
                  {resource === 'projects' && item.featured ? (
                    <span className="inline-flex items-center gap-1 rounded-md bg-beam-yellow/25 px-2 py-1 text-xs font-extrabold text-beam-ink">
                      <Star className="h-3 w-3 fill-current" aria-hidden="true" />
                      {t('admin.featured')}
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="mt-3">{rowActions(item)}</div>
          </div>
        )}
        footer={
          <Pagination
            page={pagination.page || 1}
            pages={pagination.pages || 1}
            total={pagination.total || 0}
            limit={pagination.limit || limit}
            onPage={setPage}
          />
        }
      />

      <FormDrawer
        open={showForm}
        title={editing ? t('admin.editRecord') : t('admin.newRecord', { resource: title })}
        description={t('admin.bilingualHelp')}
        onClose={requestClose}
        footer={
          <>
            <Button type="submit" form="collection-form" loading={saveMutation.isPending}>
              {t('common.save')}
            </Button>
            <Button type="button" variant="outline" onClick={requestClose}>
              {t('common.cancel')}
            </Button>
            {dirty ? (
              <span className="ml-auto text-xs font-bold text-state-warn">{t('admin.unsavedChanges')}</span>
            ) : null}
          </>
        }
      >
        <form id="collection-form" onSubmit={submit} className="space-y-6" noValidate>
          <div className="grid gap-4 lg:grid-cols-2">
            {fieldDefs(resource, t).map(([name, label, required]) => (
              <label key={name} htmlFor={`field-${name}`} className="grid gap-2 text-sm font-bold">
                <span>
                  {label}
                  {required ? <span className="text-state-critical"> *</span> : null}
                </span>
                {resource === 'testimonials' && name.startsWith('summary') ? (
                  <textarea
                    id={`field-${name}`}
                    name={name}
                    value={form[name]}
                    onChange={update}
                    rows={4}
                    aria-invalid={Boolean(errors[name])}
                    className={`focus-ring rounded-lg border px-4 py-3 outline-none ${
                      errors[name] ? 'border-state-critical' : 'border-beam-blue/10'
                    }`}
                  />
                ) : (
                  <input
                    id={`field-${name}`}
                    name={name}
                    value={form[name]}
                    onChange={update}
                    aria-invalid={Boolean(errors[name])}
                    className={`focus-ring rounded-lg border px-4 py-3 outline-none ${
                      errors[name] ? 'border-state-critical' : 'border-beam-blue/10'
                    }`}
                  />
                )}
                {errors[name] ? (
                  <span className="text-xs font-bold text-state-critical">{errors[name]}</span>
                ) : null}
              </label>
            ))}

            <label htmlFor="field-status" className="grid gap-2 text-sm font-bold">
              {t('admin.status')}
              <select
                id="field-status"
                name="status"
                value={form.status}
                onChange={update}
                className="focus-ring rounded-lg border border-beam-blue/10 px-4 py-3 outline-none"
              >
                <option value="published">{t('admin.published')}</option>
                <option value="draft">{t('common.draft')}</option>
                <option value="hidden">{t('admin.hidden')}</option>
              </select>
            </label>

            {resource === 'projects' ? (
              <label className="flex items-center gap-3 self-end rounded-lg border border-beam-blue/10 bg-beam-light/60 px-4 py-3 text-sm font-bold">
                <input
                  type="checkbox"
                  name="featured"
                  checked={form.featured}
                  onChange={update}
                  className="focus-ring h-4 w-4 accent-beam-blue"
                />
                <span>{t('admin.featured')}</span>
              </label>
            ) : null}
          </div>

          {usesGallery ? (
            <ImageUploadField
              label={
                resource === 'services'
                  ? t('admin.serviceImages')
                  : resource === 'projects'
                    ? t('admin.campaignImages')
                    : t('admin.postImages')
              }
              helper={t('admin.galleryUploadHelp')}
              value={form.gallery}
              multiple
              max={5}
              onChange={(gallery) => patchForm({ gallery, image: gallery[0] || '' })}
            />
          ) : (
            <ImageUploadField
              label={t('admin.coverImage')}
              helper={t('admin.coverUploadHelp')}
              value={form.image}
              onChange={(image) => patchForm({ image })}
            />
          )}

          {resource !== 'gallery' && resource !== 'testimonials' ? (
            <div className="grid gap-4 lg:grid-cols-2">
              <RichTextEditor
                label={t('admin.descriptionFr')}
                value={form.descriptionFr}
                onChange={(value) => patchForm({ descriptionFr: value })}
              />
              <RichTextEditor
                label={t('admin.descriptionEn')}
                value={form.descriptionEn}
                onChange={(value) => patchForm({ descriptionEn: value })}
              />
            </div>
          ) : null}

          {resource === 'services' ? (
            <section className="rounded-lg border border-beam-blue/10 bg-beam-light/40 p-4">
              <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
                <div>
                  <h3 className="text-sm font-extrabold uppercase tracking-[0.2em] text-beam-blue">
                    {t('admin.serviceBenefits')}
                  </h3>
                  <p className="mt-1 text-sm font-semibold text-beam-graphite/60">
                    {t('admin.serviceBenefitsHelp')}
                  </p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  icon={Plus}
                  onClick={() =>
                    patchForm({ benefits: [...form.benefits, { id: nextBenefitKey(), fr: '', en: '' }] })
                  }
                >
                  {t('admin.addBenefit')}
                </Button>
              </div>

              <div className="mt-4 grid gap-3">
                {form.benefits.map((benefit, index) => (
                  <div
                    key={benefit.id}
                    className="grid gap-3 rounded-lg bg-white p-3 shadow-sm lg:grid-cols-[1fr_1fr_auto] lg:items-end"
                  >
                    <label className="grid gap-2 text-sm font-bold">
                      {t('admin.benefitFr')}
                      <input
                        value={benefit.fr}
                        onChange={(event) =>
                          patchForm({
                            benefits: form.benefits.map((row, rowIndex) =>
                              rowIndex === index ? { ...row, fr: event.target.value } : row,
                            ),
                          })
                        }
                        className="focus-ring rounded-lg border border-beam-blue/10 px-4 py-3 outline-none"
                      />
                    </label>
                    <label className="grid gap-2 text-sm font-bold">
                      {t('admin.benefitEn')}
                      <input
                        value={benefit.en}
                        onChange={(event) =>
                          patchForm({
                            benefits: form.benefits.map((row, rowIndex) =>
                              rowIndex === index ? { ...row, en: event.target.value } : row,
                            ),
                          })
                        }
                        className="focus-ring rounded-lg border border-beam-blue/10 px-4 py-3 outline-none"
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() =>
                        patchForm({ benefits: form.benefits.filter((_, rowIndex) => rowIndex !== index) })
                      }
                      className="focus-ring inline-flex h-11 w-11 items-center justify-center rounded-lg bg-state-critical/10 text-state-critical"
                      aria-label={t('admin.removeBenefit')}
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>
                ))}
                {!form.benefits.length ? (
                  <p className="rounded-lg bg-white p-4 text-sm font-semibold text-beam-graphite/60">
                    {t('admin.noBenefits')}
                  </p>
                ) : null}
              </div>
            </section>
          ) : null}
        </form>
      </FormDrawer>
    </div>
  );
}
